import React, { useState } from 'react';
import {
    Search as SearchIcon,
    Add as AddIcon,
    History as HistoryIcon,
    FileUpload as ImportIcon,
    FileDownload as ExportIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    FilterAlt as FilterAltIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider
} from '@mui/material';

interface LookupItem {
    id: string;
    name: string;
    code: string;
    description: string;
    scope: string;
    status: 'Active' | 'Inactive';
    values: { code: string; name: string }[];
}

const LookupsPage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('');

    // State for Dialog
    const [open, setOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<LookupItem | null>(null);

    // Form State
    const [formData, setFormData] = useState<Omit<LookupItem, 'id' | 'status'>>({
        name: '',
        code: '',
        description: '',
        scope: '',
        values: []
    });

    // Lookup Value Input State (for adding new values inside the dialog)
    const [newValue, setNewValue] = useState<{ code: string; name: string }>({ code: '', name: '' });
    const [showValueInput, setShowValueInput] = useState(false);

    // Mock data based on screenshot
    const [items, setItems] = useState<LookupItem[]>([]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleOpenDialog = (item?: LookupItem) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                code: item.code,
                description: item.description,
                scope: item.scope,
                values: item.values || []
            });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                code: '',
                description: '',
                scope: '',
                values: []
            });
        }
        setOpen(true);
        setShowValueInput(false);
        setNewValue({ code: '', name: '' });
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setEditingItem(null);
    };

    const handleAddValue = () => {
        if (newValue.code && newValue.name) {
            setFormData(prev => ({
                ...prev,
                values: [...prev.values, { ...newValue }]
            }));
            setNewValue({ code: '', name: '' });
            setShowValueInput(false);
        }
    };

    const handleRemoveValue = (index: number) => {
        setFormData(prev => ({
            ...prev,
            values: prev.values.filter((_, i) => i !== index)
        }));
    };

    const handleSave = () => {
        if (editingItem) {
            // Update existing
            setItems(prev => prev.map(item =>
                item.id === editingItem.id
                    ? { ...item, ...formData }
                    : item
            ));
        } else {
            // Add new
            const newItem: LookupItem = {
                id: Math.random().toString(36).substr(2, 9),
                status: 'Active',
                ...formData
            };
            setItems(prev => [...prev, newItem]);
        }
        handleCloseDialog();
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                <FilterAltIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                    Lookups
                </Typography>
            </Box>

            {/* Actions Bar */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
                <Box sx={{ flexGrow: 1, maxWidth: { xs: '100%', md: 400 } }}>
                    <TextField
                        fullWidth
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                        sx={{ bgcolor: 'white' }}
                    />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                        <Tooltip title="History">
                            <IconButton sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <HistoryIcon />
                            </IconButton>
                        </Tooltip>

                        <Button
                            variant="outlined"
                            startIcon={<ExportIcon />}
                            sx={{ bgcolor: 'white', color: 'text.primary', borderColor: '#e0e0e0', flex: 1 }}
                        >
                            Export
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<ImportIcon />}
                            sx={{ bgcolor: 'white', color: 'text.primary', borderColor: '#e0e0e0', flex: 1 }}
                        >
                            Import
                        </Button>
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ bgcolor: "#48CAE4", color: "#000000", fontWeight: "normal", "&:hover": { bgcolor: "#3ab3cc" } }}
                        onClick={() => handleOpenDialog()}
                        fullWidth
                    >
                        Add New Item
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }} aria-label="lookups table">
                    <TableHead sx={{
                        "& .MuiTableCell-head": {
                            color: "#434445ff !important",


                        }
                    }}>
                        <TableRow>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Code</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Description</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Scope</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredItems.map((item) => (
                            <TableRow
                                key={item.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#f9fafb' } }}
                            >
                                <TableCell component="th" scope="row" sx={{ fontWeight: 'medium' }}>
                                    {item.name}
                                </TableCell>
                                <TableCell>{item.code}</TableCell>
                                <TableCell>{item.description}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={item.scope}
                                        size="small"
                                        sx={{
                                            bgcolor: '#f3e8ff',
                                            color: '#9333ea',
                                            fontWeight: 500,
                                            borderRadius: 1
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                                        <Typography variant="body2" fontWeight="medium">{item.status}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" color="primary" onClick={() => handleOpenDialog(item)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredItems.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">No items found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editingItem ? 'Edit Item' : 'Add New Item'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Name <span style={{ color: '#d32f2f' }}>*</span></Typography>
                            <TextField
                                fullWidth

                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Code <span style={{ color: '#d32f2f' }}>*</span></Typography>
                            <TextField
                                fullWidth

                                value={formData.code}
                                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Scope</Typography>
                            <TextField
                                fullWidth
                                value={formData.scope}
                                onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Description</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold">Lookup Values</Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowValueInput(true)}
                                    disabled={showValueInput}
                                >
                                    Add Value
                                </Button>
                            </Box>

                            {/* List of Values */}
                            {formData.values.length === 0 && !showValueInput && (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2, fontStyle: 'italic' }}>
                                    No values added yet.
                                </Typography>
                            )}

                            {formData.values.map((val, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        value={val.code}
                                        InputProps={{ readOnly: true }}
                                        sx={{ width: '30%', bgcolor: '#f8fafc' }}
                                    />
                                    <TextField
                                        size="small"
                                        value={val.name}
                                        InputProps={{ readOnly: true }}
                                        sx={{ flexGrow: 1, bgcolor: '#f8fafc' }}
                                    />
                                    <IconButton size="small" color="error" onClick={() => handleRemoveValue(index)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}

                            {/* Add New Value Inputs */}
                            {showValueInput && (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Code"
                                        value={newValue.code}
                                        onChange={(e) => setNewValue({ ...newValue, code: e.target.value })}
                                        sx={{ width: '30%' }}
                                        autoFocus
                                    />
                                    <TextField
                                        size="small"
                                        placeholder="Name/Value"
                                        value={newValue.name}
                                        onChange={(e) => setNewValue({ ...newValue, name: e.target.value })}
                                        sx={{ flexGrow: 1 }}
                                    />
                                    <IconButton size="small" color="success" onClick={handleAddValue}>
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="default" onClick={() => setShowValueInput(false)}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">Save Changes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LookupsPage;
